import { useState, useContext, useEffect } from 'react';
import api from './api/axiosInstance.js';
import { Capacitor } from '@capacitor/core';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserContext } from './UserContext';
import { Link } from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import { canonicalRole } from './utils/roleUtils';
import { identifyAnalyticsUser, trackEvent } from './services/analytics/analyticsService';

import { ANALYTICS_EVENTS } from './services/analytics/analyticsEvents';

import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import icEye from './assets/img/ic-eye.svg';
import icEyeClosed from './assets/img/ic-eye-closed.svg';
import { useMediaQuery } from 'react-responsive';
import { INCOMPLETE_PROFILE_REMINDER_SESSION_KEY } from './utils/onboarding';
import { getPostLoginDestination } from './utils/userCompletion';

const MAX_ATTEMPTS = 5;
const LOCKDOWN_TIME = 5 * 60; // 5 minutes in seconds

const PLATFORM_ADMINS = ['titanAdmin', 'superadmin'];

const Login = () => {
    const isMobile = useMediaQuery({ query: '(max-width: 767.98px)' });
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordLogin, setShowPasswordLogin] = useState(false);
    const [error, setError] = useState('');
    const [attempts, setAttempts] = useState(0);
    const [lockdown, setLockdown] = useState(false);
    const [countdown, setCountdown] = useState(LOCKDOWN_TIME);
    const [permanentlyLocked, setPermanentlyLocked] = useState(false);
    const [showLoginMessage, setShowLoginMessage] = useState(true);
    const [magicLinkMessage, setMagicLinkMessage] = useState('');
    const [magicLinkLoading, setMagicLinkLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const params = new URLSearchParams(location.search);
    const redirectPath = params.get('redirect');
    const { user, setUser } = useContext(UserContext);
    const [sessionExpiredMessage, setSessionExpiredMessage] = useState(false);
    const [magicLinkErrorMessage, setMagicLinkErrorMessage] = useState('');

    useEffect(() => {
        if (!user) return;

        const role = canonicalRole(user.role);

        if (role === 'student') {
            navigate(getPostLoginDestination(user), { replace: true });
            return;
        }

        if (role === 'donatorUser') {
            navigate('/donations-my', { replace: true });
            return;
        }

        if (isPlatformAdmin(role)) {
            if (!user.hasAgreedToTerms) {
                navigate('/terms-and-conditions-admin', { replace: true });
            } else {
                navigate('/ticket-list', { replace: true });
            }
            return;
        }

        if (role === 'company') {
            navigate('/dashboard-company', { replace: true });
            return;
        }
    }, [user, navigate]);

    const isPlatformAdmin = (role) =>
        PLATFORM_ADMINS.includes(String(role || '').trim());

    useEffect(() => {
        const flagFromSession = sessionStorage.getItem('sessionExpired') === 'true';
        const flagFromQuery = new URLSearchParams(window.location.search).get('sessionExpired') === 'true';

        if (flagFromSession || flagFromQuery) {
            setSessionExpiredMessage(true);
            sessionStorage.removeItem('sessionExpired');

            // ✅ Remove only the `sessionExpired` param, preserve others
            const params = new URLSearchParams(window.location.search);
            params.delete('sessionExpired');
            const cleanedUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
            window.history.replaceState({}, document.title, cleanedUrl);
        }

        const magicError = new URLSearchParams(window.location.search).get('magicLinkError');
        if (magicError) {
            setMagicLinkErrorMessage(decodeURIComponent(magicError));
            const params = new URLSearchParams(window.location.search);
            params.delete('magicLinkError');
            const cleanedUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
            window.history.replaceState({}, document.title, cleanedUrl);
        }
    }, []);

    useEffect(() => {
        let timer;

        if (lockdown && !permanentlyLocked) {
            setShowLoginMessage(false); // Hide login message when lockdown starts
            console.log('Lockdown started, showLoginMessage:', showLoginMessage); // Log the initial value

            timer = setInterval(() => {
                setCountdown((prev) => {
                    console.log('Countdown:', prev); // Log countdown value
                    if (prev <= 1) {
                        clearInterval(timer);
                        setLockdown(false);
                        setAttempts(0); // Reset attempts after lockdown ends
                        setShowLoginMessage(true); // Show login message after lockdown
                        console.log('Lockdown ended, showLoginMessage:', showLoginMessage); // Log value after lockdown ends
                        return LOCKDOWN_TIME;
                    }
                    return prev - 1;
                });
            }, 1000);
        }

        return () => clearInterval(timer);
    }, [lockdown, permanentlyLocked]);

    useEffect(() => {
    }, [showLoginMessage]);

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();

        if (lockdown || permanentlyLocked) return;

        try {
            const response = await api.post(`/api/auth/login`, { email, password });
            const { token, redirectTo } = response.data;

            localStorage.setItem('token', token);

            const userResponse = await api.get(`/api/users/me`);
            const user = userResponse.data;

            localStorage.setItem('user', JSON.stringify(user));
            sessionStorage.removeItem(INCOMPLETE_PROFILE_REMINDER_SESSION_KEY);
            setUser(user);

            identifyAnalyticsUser(user);

            trackEvent(ANALYTICS_EVENTS.LOGIN_COMPLETED, {
                loginMethod: 'password',
                role: user?.role || '',
                registrationCompleted: Boolean(user?.registrationCompleted),
                profileCompleted: Boolean(user?.profileCompleted),
            });

            setError('');
            setMagicLinkMessage('');
            setAttempts(0);
            setLockdown(false);
            setPermanentlyLocked(false);

            const role = canonicalRole(user?.role);

            if (redirectPath) {
                navigate(redirectPath, { replace: true });
                return;
            }

            if (redirectTo) {
                navigate(redirectTo, { replace: true });
                return;
            }

            if (role === 'student') {
                navigate(getPostLoginDestination(user), { replace: true });
                return;
            }

            if (role === 'donatorUser') {
                navigate('/donations-my', { replace: true });
                return;
            }

            if (isPlatformAdmin(role)) {
                if (!user.hasAgreedToTerms) {
                    navigate('/terms-and-conditions-admin', { replace: true });
                } else {
                    navigate('/ticket-list', { replace: true });
                }
                return;
            }

            if (role === 'company') {
                navigate('/dashboard-company', { replace: true });
                return;
            }

            navigate('/', { replace: true });
        } catch (err) {
            console.error('Login error:', err.response ? err.response.data : err.message);

            setMagicLinkMessage('');
            setError(err.response ? err.response.data.msg : 'An error occurred during login.');
            setShowLoginMessage(false);

            if (err.response && err.response.data.isLocked) {
                setLockdown(true);
                setCountdown(LOCKDOWN_TIME);
                setAttempts(MAX_ATTEMPTS);

                if (err.response.data.permanent) {
                    setPermanentlyLocked(true);
                    setLockdown(false);
                } else {
                    setPermanentlyLocked(false);
                }
            } else {
                setAttempts((prev) => {
                    const newAttempts = prev + 1;
                    if (newAttempts >= MAX_ATTEMPTS) setLockdown(true);
                    return newAttempts;
                });
            }
        }
    };

    const handleMagicLinkSubmit = async () => {
        if (lockdown || permanentlyLocked || magicLinkLoading) return;

        if (!email.trim()) {
            setMagicLinkMessage('');
            setError('Please enter your email address.');
            setShowLoginMessage(false);
            return;
        }

        try {
            setMagicLinkLoading(true);
            setError('');
            setMagicLinkMessage('');
            setShowLoginMessage(true);

            await api.post('/api/auth/magic-link/send', {
                email,
                mode: 'login',
                redirect: redirectPath || '/',
                isNative: Capacitor.isNativePlatform(),
            });

            trackEvent(ANALYTICS_EVENTS.MAGIC_LINK_REQUESTED, {
                redirect: redirectPath || '/',
            });

            setPassword('');
            setShowPassword(false);
            setShowPasswordLogin(false);
            setMagicLinkMessage('Check your email for a secure sign-in link.');
        } catch (err) {
            console.error('Magic link error:', err.response ? err.response.data : err.message);
            setMagicLinkMessage('');
            setError(
                err.response?.data?.message ||
                err.response?.data?.msg ||
                'Unable to send magic link.'
            );
            setShowLoginMessage(false);
        } finally {
            setMagicLinkLoading(false);
        }
    };
    
    return (
        <> 
        {/* preview */}
            <main className="main-content flex-grow-1 ms-0">
                <Container>
                    <Row>
                        {/* Main Content */}
                        <Col xl={4} className='offset-xl-4 pt-5'>
                            <Card>
                                <Card.Body>
                                    
                                    {/* Message handling */}
                                    {permanentlyLocked ? (
                                        <>
                                            <h2>Login permanently blocked</h2>
                                            <p>Please reset your password</p>
                                            <p><span>{email}</span></p>
                                            <div className="row mb-3">
                                                <div className="col-lg-6">
                                                    <a href='/' className="btn btn-outline-primary w-100">Back</a>
                                                </div>
                                                <div className="col-lg-6">
                                                    <a href='#' className="btn btn btn-primary w-100">Reset Password</a>
                                                </div>
                                            </div>
                                        </>
                                    ) : lockdown ? (
                                        <>
                                            <h2>Log in blocked.</h2>
                                            <p>
                                                You can try to <strong>
                                                    log in again in {Math.floor(countdown / 60)}:
                                                    {String(countdown % 60).padStart(2, '0')}
                                                </strong>
                                            </p>
                                            <p><span>{email}</span></p>
                                        </>
                                    ) : error && !showLoginMessage ? (
                                        <>
                                            <h2>Login Failed</h2>
                                            <p style={{ color: 'teal', fontWeight: 'bold' }}>{error}</p>
                                        </>
                                    ) : showLoginMessage && (
                                        <div className={isMobile ? 'mb-4' : 'mb-5'}>
                                            <h2 className="mb-3">Login now</h2>    
                                            <p>Enter your email to receive a secure login link or use your password.</p> 
                                        </div>
                                    )}
                            

                                    {/* Form rendering */}
                                    {!permanentlyLocked && !lockdown && (
                    
                                        <Form onSubmit={handlePasswordSubmit}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="text-start">Email address</Form.Label>
                                                <Form.Control
                                                    id="email"
                                                    type="email"
                                                    value={email}
                                                    onChange={(e) => {
                                                        setEmail(e.target.value);
                                                        setError('');
                                                        setMagicLinkMessage('');
                                                    }}
                                                    required
                                                    placeholder=""
                                                    autoComplete="email"
                                                />
                                            </Form.Group>

                                            {!showPasswordLogin && (
                                                <>
                                                    <Button
                                                        type="button"
                                                        variant="primary w-100 justify-content-center"
                                                        className="mb-3"
                                                        onClick={handleMagicLinkSubmit}
                                                        disabled={magicLinkLoading}
                                                    >
                                                        {magicLinkLoading ? 'Sending link...' : 'Send magic link'}
                                                    </Button>

                                                    {magicLinkMessage && (
                                                        <p className="text-center mb-4" style={{ color: 'teal', fontWeight: 'bold' }}>
                                                            {magicLinkMessage}
                                                        </p>
                                                    )}
                                                </>
                                            )}

                                            {!showPasswordLogin && (
                                                <div className="text-center">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setShowPasswordLogin(true);
                                                            setError('');
                                                            setMagicLinkMessage('');
                                                            setMagicLinkLoading(false);
                                                        }}
                                                        className={isMobile
                                                            ? 'btn h-auto btn-link p-0'
                                                            : 'btn h-auto btn-link p-0 text-decoration-none text-muted'}
                                                    >
                                                        Use password instead
                                                    </button>
                                                </div>
                                            )}

                                            {showPasswordLogin && (
                                                <>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label className="text-start">Password</Form.Label>
                                                        <div style={{ position: 'relative' }}>
                                                            <Form.Control
                                                                id="password"
                                                                type={showPassword ? 'text' : 'password'}
                                                                value={password}
                                                                onChange={(e) => setPassword(e.target.value)}
                                                                autoComplete="current-password"
                                                                style={{ paddingRight: '42px' }}
                                                                autoFocus={showPasswordLogin}
                                                            />

                                                            <img
                                                                src={showPassword ? icEye : icEyeClosed}
                                                                alt=""
                                                                width={16}
                                                                height={16}
                                                                onClick={() => setShowPassword((v) => !v)}
                                                                style={{
                                                                    position: 'absolute',
                                                                    right: '12px',
                                                                    top: '50%',
                                                                    transform: 'translateY(-50%)',
                                                                    cursor: 'pointer',
                                                                    opacity: 0.7,
                                                                }}
                                                            />
                                                        </div>
                                                    </Form.Group>

                                                    <p className="text-end mb-3">
                                                        <a href="/password-reset">
                                                            <strong className="font-size-md">Lost your password?</strong>
                                                        </a>
                                                    </p>

                                                    <Button
                                                        type="submit"
                                                        variant="primary w-100 justify-content-center"
                                                        disabled={!password.trim()}
                                                    >
                                                        Continue with password
                                                    </Button>

                                                    <div className="text-center mt-3">
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setShowPasswordLogin(false);
                                                                setError('');
                                                                setMagicLinkMessage('');
                                                                setMagicLinkLoading(false);
                                                            }}
                                                            className="btn h-auto btn-link p-0 text-decoration-none text-muted"
                                                        >
                                                            Use email link instead
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </Form>
                                    )}
                                </Card.Body>
                            </Card>
                            <Row className="buttons-row">
                                <Col xs={12}>
                                    {sessionExpiredMessage && (
                                        <>
                                            <Container>
                                                <Row>
                                                    <Col xs={12}>
                                                        <div className="alert alert-warning" role="alert">
                                                            Your session has expired. Please log in again.
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </Container>
                                        </>
                                    )}

                                    {magicLinkErrorMessage && (
                                        <>
                                            <Container>
                                                <Row>
                                                    <Col xs={12}>
                                                        <div className="alert alert-warning" role="alert">
                                                            {magicLinkErrorMessage}
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </Container>
                                        </>
                                    )}

                                    {/* Conditional links */}
                                    {!permanentlyLocked ? (
                                        <>
                                            <Container>
                                                <Row>
                                                    <Col xs={12}>
                                                        <div className="d-flex justify-content-center">
                                                                <p>
                                                                    Don't have an account?&nbsp;<Link to="/register" className="text-primary">Register</Link>
                                                                </p>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </Container>
                                        </>
                                    ) : (
                                        <>
                                            <Container>
                                                <Row>
                                                    <Col xs={12}>
                                                        <div className="d-flex justify-content-center">
                                                            <Link to="/password-reset">Reset password</Link>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </Container>
                                        </>
                                    )}
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                    
                </Container>
            </main>
        </>
    );
};

export default Login;
